/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from 'xlsx';

// --- SVG Icons ---
const Icons = {
    Chart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Save: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>,
    Print: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
};

export default function Subjectdetail() {
    const { id } = useParams();

    // --- State ---
    const [currentTerm, setCurrentTerm] = useState(1);
    const [students, setStudents] = useState([]);
    const [sections, setSections] = useState([]);
    const [sectionMarks, setSectionMarks] = useState([]);
    const [marksBeingEdited, setMarksBeingEdited] = useState({});
    const [subjectname, setSubjectName] = useState('Loading...');
    const [subjectGrade, setSubjectGrade] = useState('');
    const [subjectClass, setSubjectClass] = useState('');
    const [loading, setLoading] = useState(true);

    // UI State
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvProgress, setCsvProgress] = useState('');
    const [saveAllDisabled, setSaveAllDisabled] = useState(false);

    // Forms
    const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '' });
    const [newSection, setNewSection] = useState({ section_name: '', weight: 1, max_mark: 100 });
    const [addingSection, setAddingSection] = useState(false);

    // Debounce State
    const [sectionWeights, setSectionWeights] = useState({});
    const [sectionMaxMarks, setSectionMaxMarks] = useState({});

    // Refs - Initialize once with useRef and useCallback
    const markInputRefs = useRef({});
    const weightTimersRef = useRef({});
    const maxMarkTimersRef = useRef({});
    const getMarkInputRef = useCallback((sectionId, studentId) => {
        if (!markInputRefs.current[sectionId]) {
            markInputRefs.current[sectionId] = {};
        }
        if (!markInputRefs.current[sectionId][studentId]) {
            markInputRefs.current[sectionId][studentId] = React.createRef();
        }
        return markInputRefs.current[sectionId][studentId];
    }, []);

    // --- Helpers ---
    const calculateLevel = useCallback((percentage) => {
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    }, []);

    const levelToSymbol = useCallback((level) => {
        switch (level) {
            case 7: return 'A*';
            case 6: return 'A';
            case 5: return 'B';
            case 4: return 'C';
            case 3: return 'D';
            case 2: return 'E';
            case 1: return 'F';
            default: return level;
        }
    }, []);

    const formatGrade = useCallback((level) => {
        const gradeDisplayFormat = localStorage.getItem('gradeDisplayFormat') || 'levels';
        return gradeDisplayFormat === 'symbols' ? levelToSymbol(level) : level;
    }, [levelToSymbol]);

    // --- Calculate Stats Per Section (Optimized with Memoization) ---
    const getSectionStats = useCallback((section) => {
        const validMarks = [];
        const studentIds = new Set(); // Track unique students
        const sectionId = parseInt(section.id);

        for (const mark of sectionMarks) {
            if (parseInt(mark.section_id) === sectionId &&
                mark.mark !== '' &&
                mark.mark != null &&
                !isNaN(parseFloat(mark.mark))) {
                validMarks.push(parseFloat(mark.mark));
                studentIds.add(parseInt(mark.student_id)); // Add unique student ID
            }
        }

        if (validMarks.length === 0) return null;

        const count = studentIds.size; // Count unique students, not marks
        const max = Math.max(...validMarks);
        const min = Math.min(...validMarks);
        const sum = validMarks.reduce((a, b) => a + b, 0);
        const avg = sum / validMarks.length;
        const maxPossible = section.max_mark || 100;
        const avgPerc = (avg / maxPossible) * 100;

        return { count, max, min, avg, avgPerc };
    }, [sectionMarks]);

    // --- LOGIC: Grid Navigation (Optimized with cached indexes) ---
    const handleGridNavigation = useCallback((e, currentStudentId, currentSectionId) => {
        const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
        if (!allowedKeys.includes(e.key)) return;

        e.preventDefault();

        const studentIndex = students.findIndex(s => String(s.student_id) === String(currentStudentId));
        const sectionIndex = sections.findIndex(s => String(s.id) === String(currentSectionId));

        let nextStudentId = currentStudentId;
        let nextSectionId = currentSectionId;

        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (studentIndex < students.length - 1) nextStudentId = students[studentIndex + 1].student_id;
        } else if (e.key === 'ArrowUp') {
            if (studentIndex > 0) nextStudentId = students[studentIndex - 1].student_id;
        } else if (e.key === 'ArrowRight') {
            if (sectionIndex < sections.length - 1) nextSectionId = sections[sectionIndex + 1].id;
        } else if (e.key === 'ArrowLeft') {
            if (sectionIndex > 0) nextSectionId = sections[sectionIndex - 1].id;
        }

        const ref = getMarkInputRef(nextSectionId, nextStudentId);
        if (ref && ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, [students, sections, getMarkInputRef]);

    // --- LOGIC: Bulk Save ---
    const handleBulkSave = async () => {
        setSaveAllDisabled(true);
        try {
            // Separate marks into updates and deletes for better handling
            const marksToCreate = [];
            const marksToUpdate = [];
            const marksToDelete = [];

            // Only process marks that were actually edited (in marksBeingEdited)
            Object.entries(marksBeingEdited).forEach(([key, value]) => {
                const [studentId, sectionId] = key.split('-');
                const section = sections.find(s => String(s.id) === String(sectionId));
                const existingMark = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                // Check if mark is empty/null (should be deleted)
                if (value === '' || value === null || value === undefined) {
                    if (existingMark && existingMark.id) {
                        // Has ID - it exists in DB, needs deletion
                        marksToDelete.push(existingMark.id);
                    }
                    // Else: never saved, skip it
                } else {
                    // Has a value - create or update
                    const markData = {
                        student_id: parseInt(studentId),
                        section_id: parseInt(sectionId),
                        subject_id: id,
                        term: currentTerm,
                        mark: value,
                        max_mark: section ? section.max_mark : 100
                    };

                    if (existingMark && existingMark.id) {
                        markData.id = existingMark.id;
                        marksToUpdate.push(markData);
                    } else {
                        marksToCreate.push(markData);
                    }
                }
            });

            let totalUpdated = 0;

            // Handle deletions
            if (marksToDelete.length > 0) {
                for (const markId of marksToDelete) {
                    try {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markId}`);
                        totalUpdated++;
                    } catch (e) {
                        console.error(`Failed to delete mark ${markId}:`, e);
                    }
                }
                // Remove deleted marks from state
                setSectionMarks(prev => prev.filter(m => !marksToDelete.includes(m.id)));
            }

            // Handle creates and updates
            if (marksToCreate.length > 0 || marksToUpdate.length > 0) {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/save_bulk_marks.php`, {
                    create: marksToCreate,
                    update: marksToUpdate
                });

                if (response.data && response.data.success) {
                    // Count newly created and updated marks
                    totalUpdated += marksToCreate.length + marksToUpdate.length;

                    // Update marks with returned IDs for newly created marks
                    if (response.data.created_ids && Object.keys(response.data.created_ids).length > 0) {
                        setSectionMarks(prev => prev.map(m => {
                            const key = `${m.student_id}-${m.section_id}`;
                            const newId = response.data.created_ids[key];
                            return newId && !m.id ? { ...m, id: newId } : m;
                        }));
                    }
                } else {
                    alert('Save failed: ' + (response.data?.error || 'Unknown error'));
                }
            }

            alert(`Saved successfully! Updated/deleted ${totalUpdated} marks.`);
        } catch (error) {
            alert('Save error: ' + (error.response?.data?.error || error.message));
        } finally {
            setSaveAllDisabled(false);
        }
    };

    // --- LOGIC: Excel Upload ---
    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setCsvUploading(true);
        setCsvProgress('Reading file...');

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    alert('Excel file is empty or missing data rows');
                    setCsvUploading(false); return;
                }

                const header = jsonData[0].map(h => String(h).trim());
                const sectionColumns = header.slice(2).filter(h => h && h.length > 0);

                if (sectionColumns.length === 0) {
                    alert('Excel must have at least one section column after First Name and Last Name');
                    setCsvUploading(false); return;
                }

                let workingSections = [...sections];
                const sectionIndices = {};
                let sectionsCreated = 0;

                setCsvProgress('Analyzing sections...');

                for (let colIdx = 0; colIdx < sectionColumns.length; colIdx++) {
                    const colName = sectionColumns[colIdx];
                    const excelColIndex = colIdx + 2;

                    let existingSection = workingSections.find(s =>
                        s.section_name.toLowerCase() === colName.toLowerCase()
                    );

                    if (existingSection) {
                        sectionIndices[existingSection.id] = excelColIndex;
                    } else {
                        try {
                            const res = await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                                subject_id: id,
                                term: currentTerm,
                                section_name: colName,
                                weight: 1,
                                max_mark: 100
                            });

                            if (res.data.success && res.data.section_id) {
                                const newSection = {
                                    id: res.data.section_id, section_name: colName, weight: 1, max_mark: 100
                                };
                                workingSections.push(newSection);
                                sectionIndices[newSection.id] = excelColIndex;
                                sectionsCreated++;
                            }
                        } catch (err) { }
                    }
                }

                if (Object.keys(sectionIndices).length === 0) {
                    alert('Could not match sections.'); setCsvUploading(false); return;
                }

                const totalRows = jsonData.length - 1;

                setCsvProgress(`Processing ${totalRows} students...`);

                const batchSize = 10;
                let successCount = 0;
                let studentsCreated = 0;
                for (let batchStart = 1; batchStart < jsonData.length; batchStart += batchSize) {
                    const batchEnd = Math.min(batchStart + batchSize, jsonData.length);
                    const batchPromises = [];

                    for (let i = batchStart; i < batchEnd; i++) {
                        const row = jsonData[i];
                        if (!row || row.length < 2) continue;

                        const firstName = String(row[0] || '').trim();
                        const lastName = String(row[1] || '').trim();
                        if (!firstName || !lastName) continue;

                        const processStudent = async () => {
                            try {
                                let student = students.find(s =>
                                    s.first_name.toLowerCase() === firstName.toLowerCase() &&
                                    s.last_name.toLowerCase() === lastName.toLowerCase()
                                );

                                if (!student) {
                                    try {
                                        const addRes = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                                            subject_id: id, first_name: firstName, last_name: lastName
                                        });
                                        if (addRes.data.success) {
                                            student = { student_id: addRes.data.student_id, first_name: firstName, last_name: lastName };
                                            studentsCreated++;
                                        }
                                    } catch (e) { return; }
                                }

                                if (student) {
                                    for (const [sectionId, colIdx] of Object.entries(sectionIndices)) {
                                        const rawMark = row[colIdx];
                                        const mark = parseFloat(rawMark);
                                        if (!isNaN(mark)) {
                                            const markObj = sectionMarks.find(m =>
                                                parseInt(m.student_id) === parseInt(student.student_id) &&
                                                parseInt(m.section_id) === parseInt(sectionId)
                                            );
                                            try {
                                                if (markObj && markObj.id) {
                                                    await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, { id: markObj.id, mark: mark });
                                                } else {
                                                    await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, { student_id: student.student_id, section_id: parseInt(sectionId), mark: mark });
                                                }
                                                successCount++;
                                            } catch (err) { }
                                        }
                                    }
                                }
                            } catch (rowError) { }
                        };
                        batchPromises.push(processStudent());
                    }
                    await Promise.all(batchPromises);
                    const processed = Math.min(batchEnd - 1, totalRows);
                    setCsvProgress(`Processing... ${processed}/${totalRows}`);
                    await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
                }

                setCsvUploading(false);
                setCsvProgress('');

                // Refresh data instead of full reload
                try {
                    const [updatedSecRes, updatedMarksRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`),
                        axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`)
                    ]);
                    setSections(updatedSecRes.data.sections || []);
                    setSectionMarks(updatedMarksRes.data.marks || []);
                } catch (refreshErr) {
                    console.error('Error refreshing data:', refreshErr);
                }

                alert(`Import Result for Term ${currentTerm}:\nNew Sections: ${sectionsCreated}\nNew Students: ${studentsCreated}\nMarks Updated: ${successCount}`);

            } catch (error) {
                setCsvUploading(false);
                alert('Error processing Excel: ' + error.message);
            }
        };
    };

    // --- Computed Data (Optimized - Only recalculates when dependencies change) ---
    const studentsWithComputed = useMemo(() => {
        // Create a Map for O(1) lookups instead of O(n) finds
        const marksMap = new Map();
        for (const mark of sectionMarks) {
            marksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
        }

        return students.map(student => {
            let weightedSum = 0;
            let weightSum = 0;
            let rawMarkSum = 0;
            let rawMaxSum = 0;
            let weightedScore = 0;
            let totalWeight = 0;

            const studentId = parseInt(student.student_id);

            for (const section of sections) {
                if (section.weight === 0) continue;

                const markObj = marksMap.get(`${studentId}-${section.id}`);
                const mark = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;

                const max = section.max_mark ? parseFloat(section.max_mark) : 100;
                const weight = section.weight ? parseFloat(section.weight) : 0;

                if (mark !== null && max > 0) {
                    const percent = (mark / max) * 100;
                    weightedSum += percent * weight;
                    weightSum += weight;
                    rawMarkSum += mark;
                    rawMaxSum += max;

                    const pointsEarned = (mark / max) * weight;
                    weightedScore += pointsEarned;
                    totalWeight += weight;
                }
            }

            const average = weightSum > 0 ? weightedSum / weightSum : null;
            const level = average !== null ? calculateLevel(average) : null;

            return {
                ...student,
                average,
                level,
                rawMarkSum,
                rawMaxSum,
                weightedScore,
                totalWeight
            };
        });
    }, [students, sectionMarks, sections, calculateLevel]);

    // --- Marks Map for O(1) lookup during render ---
    const marksMap = useMemo(() => {
        const map = new Map();
        for (const mark of sectionMarks) {
            map.set(`${mark.student_id}-${mark.section_id}`, mark);
        }
        return map;
    }, [sectionMarks]);


    // --- Excel Export (Reuses computed data for better performance) ---
    const handleExportExcel = useCallback(() => {
        const wb = XLSX.utils.book_new();

        // Determine Pass Mark
        const nameLower = (subjectname || '').toLowerCase();
        const isHomeLanguage = nameLower.includes('home language') || nameLower.includes('huistaal');
        const passMark = isHomeLanguage ? 50 : 40;
        const riskThreshold = passMark + 5;

        const mainHeaders = ['First Name', 'Last Name'];
        sections.forEach(s => {
            mainHeaders.push(`${s.section_name} (${s.max_mark})`);
            mainHeaders.push(`${s.section_name} Lvl`);
        });

        mainHeaders.push('Raw Total', 'Weighted Total', 'Total %', 'Level', 'Result');

        // Create a Map for O(1) lookups
        const marksMap = new Map();
        for (const mark of sectionMarks) {
            marksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
        }

        let passedCount = 0;
        let failedCount = 0;
        let atRiskCount = 0;

        const sectionDistributions = {};
        sections.forEach(s => {
            sectionDistributions[s.id] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
        });

        const rawTotalDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
        const weightedDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };

        const mainRows = studentsWithComputed.map(student => {
            const row = [student.first_name, student.last_name];
            const studentId = parseInt(student.student_id);

            sections.forEach(section => {
                const markObj = marksMap.get(`${studentId}-${section.id}`);
                const markVal = markObj ? parseFloat(markObj.mark) : null;
                const max = section.max_mark || 100;

                row.push(markVal !== null ? markVal : '');

                let secLevel = '';
                if (markVal !== null && max > 0) {
                    const perc = (markVal / max) * 100;
                    const level = calculateLevel(perc);
                    secLevel = formatGrade(level);
                    sectionDistributions[section.id][level]++;
                }
                row.push(secLevel);
            });

            // Raw Total
            row.push(student.rawMaxSum > 0 ? `${student.rawMarkSum}` : '');
            if (student.rawMaxSum > 0) {
                const rawPerc = (student.rawMarkSum / student.rawMaxSum) * 100;
                const rawLvl = calculateLevel(rawPerc);
                rawTotalDist[rawLvl]++;
            }

            // Weighted Total
            row.push(student.totalWeight > 0 ? `${Number(student.weightedScore).toFixed(1)}` : '');

            // Final Percentage
            const finalAverage = student.average ? Math.round(student.average) : '';
            row.push(finalAverage !== '' ? finalAverage + '%' : '');

            // Level
            const lvl = student.level ? student.level : null;
            row.push(lvl ? formatGrade(lvl) : '');
            if (lvl) weightedDist[lvl]++;

            // Result Logic
            let resultStatus = '';
            if (student.average !== null) {
                const roundedAvg = Math.round(student.average);
                if (roundedAvg < passMark) {
                    failedCount++;
                    resultStatus = 'Fail';
                } else if (roundedAvg >= passMark && roundedAvg <= riskThreshold) {
                    atRiskCount++;
                    passedCount++;
                    resultStatus = 'At Risk';
                } else {
                    passedCount++;
                    resultStatus = 'Pass';
                }
            }
            row.push(resultStatus);

            return row;
        });

        // Section statistics in the same sheet
        mainRows.push([]);
        mainRows.push(['SECTION STATISTICS']);
        mainRows.push(['Section Name', 'Average Mark', 'Average %', 'Highest Mark', 'Lowest Mark', 'Students Submitted']);

        sections.forEach(section => {
            const stats = getSectionStats(section);
            if (stats) {
                mainRows.push([
                    section.section_name,
                    stats.avg.toFixed(1),
                    stats.avgPerc.toFixed(1) + '%',
                    stats.max,
                    stats.min,
                    `${stats.count} / ${students.length}`
                ]);
            }
        });

        // Summary Statistics
        mainRows.push([]);
        mainRows.push(['SUMMARY STATISTICS']);
        mainRows.push(['Pass Requirement', `${passMark}%`]);
        mainRows.push(['Total Learners', students.length]);
        mainRows.push(['Total Passed', passedCount]);
        mainRows.push(['Total At Risk', atRiskCount]);
        mainRows.push(['Total Failed', failedCount]);

        // Levels Distribution
        mainRows.push([]);
        mainRows.push(['LEVELS DISTRIBUTION ANALYSIS']);

        const distHeader = ['Level Description', 'Percentage Range'];
        sections.forEach(s => distHeader.push(`${s.section_name}`));
        distHeader.push('Raw Total Dist', 'Weighted Total Dist');
        mainRows.push(distHeader);

        const levelMetadata = [
            { lvl: 7, desc: 'Outstanding Achievement', range: '80 - 100%' },
            { lvl: 6, desc: 'Meritorious Achievement', range: '70 - 79%' },
            { lvl: 5, desc: 'Substantial Achievement', range: '60 - 69%' },
            { lvl: 4, desc: 'Adequate Achievement', range: '50 - 59%' },
            { lvl: 3, desc: 'Moderate Achievement', range: '40 - 49%' },
            { lvl: 2, desc: 'Elementary Achievement', range: '30 - 39%' },
            { lvl: 1, desc: 'Not Achieved', range: '0 - 29%' },
        ];

        levelMetadata.forEach(meta => {
            const distRow = [`Level ${meta.lvl}: ${meta.desc}`, meta.range];
            sections.forEach(s => distRow.push(sectionDistributions[s.id][meta.lvl]));
            distRow.push(rawTotalDist[meta.lvl]);
            distRow.push(weightedDist[meta.lvl]);
            mainRows.push(distRow);
        });

        const marksSheet = XLSX.utils.aoa_to_sheet([mainHeaders, ...mainRows]);
        XLSX.utils.book_append_sheet(wb, marksSheet, `Term ${currentTerm} Marks`);

        XLSX.writeFile(wb, `${(subjectname || 'Subject').replace(/\s+/g, '_')}_${(subjectClass || 'Class').replace(/\s+/g, '_')}_Term_${currentTerm}_Report.xlsx`);
    }, [studentsWithComputed, sections, sectionMarks, calculateLevel, formatGrade, getSectionStats, currentTerm, subjectname, subjectClass, students.length]);

    const handleDownloadTemplate = () => {
        if (!sections.length && !students.length) { alert('Add sections or students first.'); return; }
        const headers = ['First Name', 'Last Name', ...sections.map(s => s.section_name)];
        const rows = [headers];
        students.forEach(s => {
            const row = [s.first_name, s.last_name];
            sections.forEach(() => row.push(''));
            rows.push(row);
        });
        const csvContent = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Template_${(subjectname || 'Subject').replace(/\s+/g, '_')}_Term${currentTerm}.csv`;
        link.click();
    };

    // --- Section Management Handlers ---
    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSection.section_name.trim()) return alert('Name required');
        setAddingSection(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                subject_id: id,
                term: currentTerm,
                ...newSection
            });
            setNewSection({ section_name: '', weight: 1, max_mark: 100 });
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`);
            setSections(res.data.sections || []);
        } catch (e) { alert('Failed'); }
        finally { setAddingSection(false); }
    };

    const handleUpdateSection = (id, field, val) => {
        if (field === 'weight') setSectionWeights(p => ({ ...p, [id]: val }));
        if (field === 'max_mark') setSectionMaxMarks(p => ({ ...p, [id]: val }));

        const timerRef = field === 'weight' ? weightTimersRef : maxMarkTimersRef;
        if (timerRef.current[id]) clearTimeout(timerRef.current[id]);

        timerRef.current[id] = setTimeout(async () => {
            try {
                const section = sections.find(s => s.id === id);
                if (!section) return;

                await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                    id,
                    section_name: section.section_name,
                    weight: field === 'weight' ? val : section.weight,
                    max_mark: field === 'max_mark' ? val : section.max_mark
                });

                setSections(prev => prev.map(s =>
                    s.id === id ? { ...s, [field]: parseFloat(val) } : s
                ));

                if (field === 'weight') {
                    setSectionWeights(prev => { const u = { ...prev }; delete u[id]; return u; });
                }
                if (field === 'max_mark') {
                    setSectionMaxMarks(prev => { const u = { ...prev }; delete u[id]; return u; });
                }

            } catch (e) {
                alert('Update failed. Check your connection.');
            }
        }, 1500);
    };

    const handleDeleteSection = async (sid, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/task_sections.php?id=${sid}`);
            setSections(prev => prev.filter(s => s.id !== sid));
        } catch (e) { alert('Error deleting'); }
    };

    // --- Mark Handlers (Optimized) ---
    const handleSectionMarkChange = useCallback((studentId, sectionId, value) => {
        const key = `${studentId}-${sectionId}`;
        let sanitized = value.replace(/[^0-9.]/g, '');

        // Allow empty string (for deletion)
        if (sanitized === '') {
            setMarksBeingEdited(prev => ({ ...prev, [key]: '' }));
            setSectionMarks(prev => {
                const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
                if (idx !== -1) {
                    const newMarks = [...prev];
                    newMarks[idx] = { ...newMarks[idx], mark: '' };
                    return newMarks;
                }
                return prev;
            });
            return;
        }

        // Validate and cap the value
        if (!isNaN(sanitized)) {
            let num = parseFloat(sanitized);
            const section = sections.find(s => s.id === sectionId);
            const max = section?.max_mark || 100;
            if (num > max) num = max;
            sanitized = String(num);
        }

        setMarksBeingEdited(prev => ({ ...prev, [key]: sanitized }));

        setSectionMarks(prev => {
            const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
            if (idx !== -1 && prev[idx].mark === sanitized) return prev; // Prevent unnecessary updates

            const newMarks = [...prev];
            if (idx !== -1) {
                newMarks[idx] = { ...newMarks[idx], mark: sanitized };
            } else {
                newMarks.push({ student_id: parseInt(studentId), section_id: parseInt(sectionId), mark: sanitized });
            }
            return newMarks;
        });
    }, [sections]);

    const handleMarkBlur = useCallback(async (studentId, sectionId) => {
        const key = `${studentId}-${sectionId}`;
        const val = marksBeingEdited[key];
        if (val !== undefined) {
            try {
                // Find the existing mark record
                const markObj = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                if (val === '' || val === null) {
                    // Empty value - DELETE the mark if it exists
                    if (markObj && markObj.id) {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markObj.id}`);
                        // Remove from state
                        setSectionMarks(prev => prev.filter(m => m.id !== markObj.id));
                    }
                } else {
                    // Non-empty value - POST or PUT
                    if (markObj && markObj.id) {
                        // Update existing mark
                        await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            id: markObj.id,
                            mark: val
                        });
                    } else {
                        // Create new mark
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            student_id: studentId,
                            section_id: sectionId,
                            mark: val
                        });
                        // Update the mark in state with the new ID from response
                        if (response.data.id) {
                            setSectionMarks(prev => {
                                const idx = prev.findIndex(m =>
                                    parseInt(m.student_id) === parseInt(studentId) &&
                                    parseInt(m.section_id) === parseInt(sectionId)
                                );
                                if (idx !== -1) {
                                    const newMarks = [...prev];
                                    newMarks[idx] = { ...newMarks[idx], id: response.data.id };
                                    return newMarks;
                                }
                                return prev;
                            });
                        }
                    }
                }

                setMarksBeingEdited(prev => {
                    const u = { ...prev };
                    delete u[key];
                    return u;
                });
            } catch (e) {
                console.error('Mark save error:', e);
                alert('Error saving mark: ' + (e.response?.data?.error || e.message));
            }
        }
    }, [marksBeingEdited, sectionMarks]);

    // --- Student Handlers ---
    const handleAddStudent = async () => {
        if (!newStudent.firstName) return alert('Name required');
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                subject_id: id, first_name: newStudent.firstName, last_name: newStudent.lastName
            });
            if (res.data.success) {
                const newStudentData = {
                    student_id: res.data.student_id,
                    first_name: newStudent.firstName,
                    last_name: newStudent.lastName,
                    level: null,
                    average: null
                };
                setStudents(prev => [...prev, newStudentData]);
                setNewStudent({ firstName: '', lastName: '' });
                alert('Student added successfully!');
            } else {
                alert('Failed to add student');
            }
        } catch (e) { alert('Error adding student: ' + e.message); }
    };

    const handleDeleteStudent = async (sid, name) => {
        if (!window.confirm(`Remove student ${name || ''}?`)) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_student.php`, { student_id: sid, subject_id: id });
            setStudents(prev => prev.filter(s => s.student_id !== sid));
        } catch (e) { alert('Error'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Delete ALL DATA?')) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_all_students.php`, { subject_id: id });
            setStudents([]);
            setSectionMarks([]);
            alert('All students and marks deleted successfully!');
        } catch (e) { alert('Error deleting: ' + e.message); }
    };

    // --- Initial Load (Optimized) ---
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [classRes, secRes, marksRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`),
                    axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`)
                ]);

                // Set students data
                const studentsData = (classRes.data.students || []).map(s => ({
                    ...s,
                    level: s.average ? calculateLevel(s.average) : null
                }));
                setStudents(studentsData);
                setSubjectName(classRes.data.subject_name);
                setSubjectGrade(classRes.data.grade);
                setSubjectClass(classRes.data.class_name);

                // Set sections and weights
                const sectionsData = secRes.data.sections || [];
                setSections(sectionsData);

                const weights = {};
                const maxes = {};
                sectionsData.forEach(s => {
                    weights[s.id] = s.weight;
                    maxes[s.id] = s.max_mark;
                });
                setSectionWeights(weights);
                setSectionMaxMarks(maxes);

                // Set marks
                setSectionMarks(marksRes.data.marks || []);

            } catch (e) {
                console.error('Load error:', e);
            }
            setLoading(false);
        };
        init();
    }, [id, currentTerm, calculateLevel]);

    return (
        <div className="subject-detail-wrapper">
            <style>{`
                :root {
                    --primary-green: #4CAF50;
                    --dark-green: #388E3C;
                    --light-green: #E8F5E9;
                    --text-dark: #2c3e50;
                    --text-light: #7f8c8d;
                    --border-color: #ecf0f1;
                    --card-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                }

                body { background-color: #f8f9fa; font-family: 'Inter', -apple-system, sans-serif; color: var(--text-dark); }
                
                .subject-detail-wrapper {
                    padding: 2rem;
                    margin-left: 220px; 
                    max-width: 1600px;
                }

                .page-header {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .page-header h1 { margin: 0; font-size: 1.5rem; font-weight: 700; color: var(--text-dark); }
                .badge { background: var(--light-green); color: var(--dark-green); padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.85rem; margin-left: 10px; }

                .term-tabs {
                    display: flex;
                    gap: 10px;
                    background: #f1f3f5;
                    padding: 5px;
                    border-radius: 25px;
                }
                .term-tab {
                    padding: 8px 24px;
                    border: none;
                    border-radius: 20px;
                    font-weight: 600;
                    color: #7f8c8d;
                    background: transparent;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                }
                .term-tab.active {
                    background: white;
                    color: var(--primary-green);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .term-tab:hover:not(.active) {
                    color: var(--text-dark);
                }

                .management-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                .modern-card { background: white; border-radius: 12px; padding: 1.5rem; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); }
                .card-title { font-size: 1.1rem; font-weight: 600; margin-top: 0; margin-bottom: 1rem; color: var(--text-dark); border-bottom: 2px solid var(--light-green); padding-bottom: 0.5rem; display: inline-block; }
                .modern-input { width: 100%; padding: 0.6rem; border: 1px solid #dfe6e9; border-radius: 6px; font-size: 0.9rem; box-sizing: border-box; }
                .modern-input:focus { outline: none; border-color: var(--primary-green); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1); }
                .btn { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 0.9rem; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
                .btn-primary { background: var(--primary-green); color: white; width: 100%; }
                .btn-outline { background: transparent; border: 1px solid var(--primary-green); color: var(--primary-green); width: 100%; }
                .btn-danger { background: #fee2e2; color: #dc2626; width: auto; padding: 0.4rem 0.8rem; font-size: 0.8rem; }
                
                .table-container { background: white; border-radius: 12px; box-shadow: var(--card-shadow); overflow-x: scroll; overflow-y: auto; border: 1px solid var(--border-color); margin-bottom: 30px; max-height: 65vh; }
                .modern-table { width: 100%; border-collapse: collapse; min-width: 800px; }
                .modern-table th { background: #f8f9fa; padding: 1rem; text-align: center; font-weight: 600; color: var(--text-dark); border-bottom: 2px solid var(--border-color); white-space: nowrap; }
                .modern-table td { padding: 0.5rem; border-bottom: 1px solid var(--border-color); text-align: center; color: var(--text-dark); }
                .cell-input { width: 100%; padding: 8px; border: 1px solid transparent; border-radius: 4px; text-align: center; font-weight: 500; background: transparent; }
                .cell-input:focus { background: white; border-color: var(--primary-green); outline: none; }
                
                .analytics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
                .stat-card { background: white; border-radius: 12px; padding: 24px; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); border-top: 4px solid var(--primary-green); display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
                .stat-grid-inner { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; row-gap: 16px; margin-bottom: 20px; }
                .stat-item { display: flex; flex-direction: column; }
                .stat-label { font-size: 0.75rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
                .stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text-dark); }
                .stat-value.green { color: var(--primary-green); }
                .stat-value.red { color: #e74c3c; }
                .submission-count { margin-top: auto; padding-top: 15px; border-top: 1px dashed #eee; font-size: 0.85rem; color: var(--text-light); display: flex; align-items: center; gap: 8px; font-weight: 500; }
                
                .loading-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-light); }
                .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid var(--primary-green); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @media print { .no-print { display: none !important; } .subject-detail-wrapper { margin: 0; padding: 0; } }

                /* --- MOBILE RESPONSIVE FIXES --- */
                @media screen and (max-width: 768px) {
                    .subject-detail-wrapper {
                        margin-left: 0;
                        padding: 10px;
                        width: 100%;
                    }
                    .page-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                        padding: 1rem;
                    }
                    .page-header h1 {
                        font-size: 1.2rem;
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                    }
                    .page-header > div { width: 100%; }
                    .badge { margin-left: 0; align-self: flex-start; }
                    .term-tabs {
                        width: 100%;
                        overflow-x: auto;
                        padding-bottom: 5px;
                        justify-content: flex-start;
                    }
                    .term-tab {
                        white-space: nowrap;
                        flex-shrink: 0;
                        padding: 6px 16px;
                    }
                    .management-grid {
                        grid-template-columns: 1fr;
                    }
                    .page-header button {
                        width: 100%;
                        margin-bottom: 5px;
                    }
                    form[style*="display: flex"] {
                        flex-direction: column;
                        align-items: stretch !important;
                    }
                    form[style*="display: flex"] input,
                    form[style*="display: flex"] button {
                        width: 100% !important;
                        flex: none !important;
                        margin-bottom: 5px;
                    }
                    .table-container {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin-bottom: 1rem;
                    }
                    .modern-table {
                        min-width: 600px;
                    }
                    .analytics-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>{subjectname} <span className="badge">{subjectGrade} {subjectClass}</span></h1>
                </div>

                {/* Term Selector Tabs */}
                <div className="term-tabs no-print">
                    {[1, 2, 3, 4].map(term => (
                        <button
                            key={term}
                            className={`term-tab ${currentTerm === term ? 'active' : ''}`}
                            onClick={() => setCurrentTerm(term)}
                        >
                            Term {term}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline no-print" style={{ width: 'auto' }} onClick={() => window.print()}>
                        <Icons.Print /> Print Sheet
                    </button>
                    <button className="btn btn-danger no-print" onClick={handleDeleteAll}>
                        <Icons.Trash /> Reset All
                    </button>
                </div>
            </div>

            <div className="no-print management-grid">

                {/* CARD 1: File Operations */}
                <div className="modern-card">
                    <h3 className="card-title"> Import / Export (Term {currentTerm})</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <button className="btn btn-outline" onClick={handleDownloadTemplate}>⬇ CSV Template</button>
                            <button className="btn btn-outline" onClick={handleExportExcel}>Export Report</button>

                        </div>

                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px dashed #ced4da', textAlign: 'center' }}>
                            {csvUploading ? (
                                <div style={{ color: 'var(--primary-green)', fontWeight: '600' }}>
                                    <div className="spinner" style={{ width: '20px', height: '20px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', marginBottom: 0 }}></div>
                                    {csvProgress}
                                </div>
                            ) : (
                                <>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#666' }}>Upload Data for Term {currentTerm}</label>
                                    <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelUpload} className="modern-input" />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* CARD 2: Add Student (Global) */}
                <div className="modern-card">
                    <h3 className="card-title"> Add Student</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input className="modern-input" placeholder="First Name" value={newStudent.firstName} onChange={e => setNewStudent({ ...newStudent, firstName: e.target.value })} />
                        <input className="modern-input" placeholder="Last Name" value={newStudent.lastName} onChange={e => setNewStudent({ ...newStudent, lastName: e.target.value })} />
                        <button className="btn btn-primary" onClick={handleAddStudent}>+ Add Student</button>
                    </div>
                </div>

                {/* CARD 3: Manage Sections (Dynamic per Term) */}
                <div className="modern-card" style={{ gridColumn: '1 / -1' }}>
                    <h3 className="card-title"> Assessment Sections (Term {currentTerm})</h3>

                    {/* Add Section Bar */}
                    <form onSubmit={handleAddSection} style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8f9fa', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                        <input className="modern-input" placeholder={`New Section for Term ${currentTerm}`} style={{ flex: 2 }} value={newSection.section_name} onChange={e => setNewSection({ ...newSection, section_name: e.target.value })} />
                        <input type="number" className="modern-input" placeholder="Weight" style={{ flex: 1 }} value={newSection.weight} onChange={e => setNewSection({ ...newSection, weight: e.target.value })} />
                        <input type="number" className="modern-input" placeholder="Max" style={{ flex: 1 }} value={newSection.max_mark} onChange={e => setNewSection({ ...newSection, max_mark: e.target.value })} />
                        <button className="btn btn-primary" style={{ width: 'auto', padding: '0.6rem 1.5rem' }} disabled={addingSection}>Add</button>
                    </form>

                    {/* Section List */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        {sections.length === 0 && <p style={{ color: '#999', padding: '10px' }}>No sections for Term {currentTerm} yet.</p>}
                        {sections.map(s => (
                            <div key={s.id} style={{ background: 'white', border: '1px solid #eee', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ fontWeight: '600', color: 'var(--text-dark)' }}>{s.section_name}</div>
                                <div style={{ display: 'flex', gap: '5px', fontSize: '0.8rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', color: '#999' }}>Weight</label>
                                        <input className="modern-input" type="number" step="0.1" value={sectionWeights[s.id] ?? s.weight} onChange={e => handleUpdateSection(s.id, 'weight', e.target.value)} style={{ padding: '4px' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', color: '#999' }}>Max</label>
                                        <input className="modern-input" type="number" value={sectionMaxMarks[s.id] ?? s.max_mark} onChange={e => handleUpdateSection(s.id, 'max_mark', e.target.value)} style={{ padding: '4px' }} />
                                    </div>
                                </div>
                                <button onClick={() => handleDeleteSection(s.id, s.section_name)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '0.8rem', cursor: 'pointer', textAlign: 'right', marginTop: '5px' }}>Delete</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Data Table */}
            {loading ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading Term {currentTerm} data...</p>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <span style={{ fontWeight: '600', color: '#666', padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>To search for a learner press Ctrl+F, for macOS,Cmd+F</span>
                        <div style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#666' }}>Term {currentTerm} Marks</span>


                            <button className="btn btn-primary no-print" style={{ width: 'auto' }} onClick={handleBulkSave} disabled={saveAllDisabled}>
                                <Icons.Save /> {saveAllDisabled ? 'Saving...' : 'Save Changes'}
                            </button>

                        </div>



                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', paddingLeft: '1.5rem', width: '200px' }}>Student Name</th>
                                    {sections.map(s => (
                                        <th key={s.id}>
                                            <div style={{ fontSize: '0.95rem' }}>{s.section_name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '400' }}>Max: {s.max_mark}</div>
                                        </th>
                                    ))}
                                    <th style={{ width: '100px', background: '#e8f5e9', borderLeft: '2px solid #dfe6e9' }}>Raw Total</th>
                                    <th style={{ width: '100px', background: '#fff3e0', borderLeft: '1px solid #dfe6e9' }}>Wgt Total</th>
                                    <th style={{ width: '80px', background: '#e8f5e9' }}>%</th>
                                    <th style={{ width: '60px', background: '#e8f5e9' }}>Lvl</th>
                                    <th className="no-print" style={{ width: '60px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsWithComputed.map(student => (
                                    <tr key={student.student_id}>
                                        <td style={{ textAlign: 'left', paddingLeft: '1.5rem', fontWeight: '500' }}>
                                            {student.first_name} {student.last_name}
                                        </td>
                                        {sections.map(s => {
                                            const key = `${student.student_id}-${s.id}`;
                                            const markObj = marksMap.get(key);
                                            const val = marksBeingEdited[key] !== undefined ? marksBeingEdited[key] : (markObj?.mark ?? '');
                                            const ref = getMarkInputRef(s.id, student.student_id);

                                            return (
                                                <td key={s.id} style={{ padding: 0 }}>
                                                    <input
                                                        ref={ref}
                                                        className="cell-input"
                                                        value={val}
                                                        placeholder="-"
                                                        onChange={e => handleSectionMarkChange(student.student_id, s.id, e.target.value)}
                                                        onBlur={() => handleMarkBlur(student.student_id, s.id)}
                                                        onKeyDown={e => handleGridNavigation(e, student.student_id, s.id)}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* Raw Total */}
                                        <td style={{ fontWeight: 'bold', color: '#555', background: '#f8f9fa', borderLeft: '2px solid #dfe6e9' }}>
                                            {student.rawMaxSum > 0 ? (
                                                <span>{Number(student.rawMarkSum).toFixed(1)} <span style={{ fontSize: '0.8em', color: '#999' }}>/ {student.rawMaxSum}</span></span>
                                            ) : '-'}
                                        </td>

                                        {/* Weighted Total */}
                                        <td style={{ fontWeight: 'bold', color: '#e67e22', background: '#fff8e1', borderLeft: '1px solid #dfe6e9' }}>
                                            {student.totalWeight > 0 ? (
                                                <span>
                                                    {Number(student.weightedScore).toFixed(1)}
                                                    <span style={{ fontSize: '0.8em', color: '#999' }}> / {student.totalWeight}</span>
                                                </span>
                                            ) : '-'}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold', color: student.average >= 50 ? 'var(--primary-green)' : '#e74c3c' }}>
                                            {student.average !== null ? Math.round(student.average) + '%' : '-'}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold' }}>
                                            <span style={{
                                                display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%',
                                                background: student.level >= 4 ? 'var(--light-green)' : '#fee2e2',
                                                color: student.level >= 4 ? 'var(--dark-green)' : '#dc2626',
                                                fontSize: '0.85rem'
                                            }}>
                                                {formatGrade(student.level)}
                                            </span>
                                        </td>
                                        <td className="no-print">
                                            <button className="btn-danger" style={{ borderRadius: '4px', padding: '4px 8px' }} onClick={() => handleDeleteStudent(student.student_id, student.first_name)}>×</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Performance Analytics (Dynamic per Term) */}
                    {sections.length > 0 && (
                        <div className="no-print" style={{ marginTop: '40px' }}>
                            <h3 className="stats-section-title"><Icons.Chart /> Term {currentTerm} Analytics</h3>
                            <div className="analytics-grid">
                                {sections.map(section => {
                                    const stats = getSectionStats(section);
                                    if (!stats) return null;

                                    return (
                                        <div key={section.id} className="stat-card">
                                            <h4>{section.section_name}</h4>
                                            <div className="stat-grid-inner">
                                                <div className="stat-item">
                                                    <span className="stat-label">Average Mark</span>
                                                    <span className="stat-value">{stats.avg.toFixed(1)}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Avg %</span>
                                                    <span className="stat-value green">{stats.avgPerc.toFixed(1)}%</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Highest</span>
                                                    <span className="stat-value">{stats.max}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Lowest</span>
                                                    <span className="stat-value red">{stats.min}</span>
                                                </div>
                                            </div>
                                            <div className="submission-count">
                                                <Icons.Check /> Submitted: <strong>{stats.count}</strong> / {students.length} students
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}